import { BrandVariants, GriffelStyle, Theme, createLightTheme, makeStyles, tokens } from '@fluentui/react-components';

// =============================================================================
// THEMING ARCHITECTURE - READ THIS FIRST
// =============================================================================
//
// This file defines the color tokens and theme for the AI Assistant app.
// We use a layered approach that combines Fluent UI theming with custom tokens.
//
// ## When to Use What
//
// 1. FLUENT TOKENS (tokens.*) - Use for standard UI elements
//    - Text colors: tokens.colorNeutralForeground1, tokens.colorNeutralForeground2, etc.
//    - Backgrounds: tokens.colorNeutralBackground1, tokens.colorBrandBackground, etc.
//    - Borders: tokens.colorNeutralStroke1, tokens.colorBrandStroke1, etc.
//    - Spacing, typography, shadows: tokens.spacingHorizontalM, tokens.fontSizeBase400, etc.
//
//    Example:
//      backgroundColor: tokens.colorNeutralBackground1,
//      color: tokens.colorNeutralForeground1,
//
// 2. CSS VARIABLES (var(--app-*)) - Use for app-specific values with no Fluent equivalent
//    - Header gradient: var(--app-header-background)
//    - Navigation: var(--app-nav-tab-active-background), var(--app-nav-tab-hover-background)
//    - Chat backgrounds: var(--app-user-message-background), var(--app-bot-icon-background)
//    - Shadows: var(--app-shadow-card), var(--app-shadow-card-subtle)
//    - Other: var(--app-send-button-active-icon-color), var(--app-hover-accordion-background)
//
//    These are defined in CustomTokens interface and injected via useAppTheme().
//
//    Example:
//      backgroundColor: 'var(--app-user-message-background)',
//      boxShadow: 'var(--app-shadow-card)',
//
// 3. DIRECT THEME ACCESS - AVOID except at the app root
//    Don't import useAppTheme() in components just to access theme.propertyName.
//    Instead, add the property to CustomTokens and use the CSS variable.
//
// ## How It Works
//
// 1. spirisLightTheme defines all colors (Spiris Design System)
// 2. useAppTheme() hook provides the theme and converts CustomTokens to CSS variables
// 3. App.tsx applies these as inline styles on the FluentProvider
// 4. Components use tokens.* for Fluent colors, var(--app-*) for custom ones
//
// ## Adding New Custom Colors
//
// 1. Add property to CustomTokens interface with JSDoc comment
// 2. Add value to spirisLightTheme
// 3. Add key to CSS_VAR_TOKEN_KEYS array in useAppTheme.ts
// 4. Use var(--app-your-token-name) in component styles (camelCase → kebab-case)
// 5. Update COLOR_TOKEN_MAPPING.md if it maps to a Design System color
//
// =============================================================================

// =============================================================================
// BRAND RAMPS
// =============================================================================

/** Spiris brand ramp (new design system) */
export const spirisBrandRamp: BrandVariants = {
    10: '#2c0753',
    20: '#441774',
    30: '#5c2a98',
    40: '#743cba',
    50: '#8e59d3',
    60: '#a777e8',
    70: '#be95f7',
    80: '#d5b3ff',
    90: '#e3ccff',
    100: '#f2e7ff',
    110: '#f5edff',
    120: '#f8f3ff',
    130: '#faf7ff',
    140: '#fcfaff',
    150: '#fdfcff',
    160: '#fefeff',
};

// =============================================================================
// CUSTOM TOKENS INTERFACE
// =============================================================================

/**
 * Semantic tokens for chat-specific styling.
 * Components should use these via CSS variables (var(--app-*)) for values
 * that have no direct Fluent UI token equivalent.
 * For standard UI elements, use Fluent's built-in tokens directly.
 */
export interface CustomTokens {
    // --- Header & Navigation ---
    /** Standalone mode header background (gradient) */
    headerBackground: string;
    /** Left navigation: selected tab background (primary-300) */
    navTabActiveBackground: string;
    /** Left navigation: hover background for inactive tab (primary-100) */
    navTabHoverBackground: string;

    // --- Chat Messages ---
    /** User message bubble background (secondary-200, coral tint) */
    userMessageBackground: string;
    /** Bot avatar icon background (gradient) */
    botIconBackground: string;
    /** Support (human agent) message bubble background — visually distinct from
     *  both the AI bot's subtle gray and the user's coral, signals "this is a
     *  different participant" on the customer side. */
    supportMessageBackground: string;

    // --- Shadows ---
    shadowCard: string;
    shadowCardSubtle: string;

    // --- Send Button (Chat Input) ---
    /** Icon color for send button when active (white on brand bg) */
    sendButtonActiveIconColor: string;

    // --- Guidance Card Accordion ---
    /** Accordion header hover background (primary-100) */
    hoverAccordionBackground: string;
}

// =============================================================================
// FAB CONFIG (Embed floating action button)
// =============================================================================
// Used only by app_integration.ts — not exposed as CSS variables.

export const fabConfig = {
    background: 'linear-gradient(180deg, #FFE9E7 0%, #FFD2D1 25%, #FFB0AC 50%, #FFA19C 75%, #F2B3FF 100%)',
    backgroundHover: 'linear-gradient(180deg, #FFE9E7 0%, #FFD2D1 25%, #FFB0AC 50%, #FFA19C 75%, #F2B3FF 100%)',
    iconColor: '#1d1d1e',
    iconScale: '1.3',
    iconScaleHover: '1',
};

// =============================================================================
// SPIRIS LIGHT THEME (New Design System)
// =============================================================================

export const spirisLightTheme: Theme & CustomTokens = {
    ...createLightTheme(spirisBrandRamp),

    // -------------------------------------------------------------------------
    // FLUENT TOKEN OVERRIDES - These automatically update all Fluent components
    // -------------------------------------------------------------------------

    // Backgrounds
    colorNeutralBackground1: '#ffffff', // Cards, inputs, elevated surfaces
    colorNeutralBackground2: '#f5f3f0', // Secondary surfaces
    colorNeutralBackground3: '#FBFAF9', // Main page background (Spiris neutral-100)
    colorNeutralBackground4: '#eeece9', // Tertiary surfaces
    colorSubtleBackground: 'transparent', // Bot message background

    // Text
    colorNeutralForeground1: '#1d1d1e', // Primary text (Spiris neutral-1100)
    colorNeutralForeground2: '#56565a', // Secondary text (Spiris neutral-900)
    colorNeutralForeground3: '#707073', // Tertiary/muted text (Spiris neutral-800)
    colorNeutralForeground4: '#8b8b8b', // Placeholder text (Spiris neutral-700)
    colorNeutralForegroundDisabled: '#8b8b8b', // Disabled text/icons (Spiris neutral-700)

    // Brand colors (buttons, links, accents)
    colorBrandBackground: '#5c2a98', // Primary brand (Spiris primary-800)
    colorBrandBackgroundHover: '#8e59d3', // Brand hover (Spiris primary-600)
    colorBrandBackgroundPressed: '#5c2a98', // Brand pressed (Spiris primary-800)
    colorBrandForeground1: '#8e59d3', // Brand text/icons
    colorBrandForeground2: '#743cba', // Brand text hover
    colorBrandForegroundLink: '#3c57b9', // Link text/icons (Spiris blue-700)
    colorBrandForegroundLinkHover: '#2a4097', // Link hover (Spiris blue-800)
    colorBrandForegroundLinkPressed: '#2a4097', // Link pressed (Spiris blue-800)

    // Borders
    colorNeutralStroke1: '#d4d3cd', // Primary borders (Spiris neutral-500)
    colorNeutralStroke2: '#e8e6e3', // Subtle borders (Spiris neutral-400)
    colorNeutralStrokeAccessible: '#39393c', // Hover borders (Spiris neutral-1000)
    colorNeutralStrokeDisabled: '#c3c1bb', // Disabled borders (Spiris neutral-600)
    colorBrandStroke1: '#a777e8', // Brand borders (Spiris primary-500)

    // Font (Spiris only)
    fontFamilyBase: 'Inter Variable, Inter, sans-serif',
    fontFamilyMonospace: 'Inter Variable, Inter, sans-serif',
    fontFamilyNumeric: 'Inter Variable, Inter, sans-serif',

    // -------------------------------------------------------------------------
    // CUSTOM TOKENS - Values with no Fluent token equivalent
    // -------------------------------------------------------------------------

    // Header (gradient background — can't be a Fluent token)
    headerBackground: 'linear-gradient(90deg, #e3ccff, #be95f7, #8e59d3, #5c2a98, #441774)',

    // Left navigation
    navTabActiveBackground: '#D5B3FF', // primary-300
    navTabHoverBackground: '#F2E7FF', // primary-100

    // Chat Messages
    userMessageBackground: '#ffe9e7', // Coral tint (Spiris secondary-200)
    botIconBackground: 'linear-gradient(0deg, #FFE9E7 0%, #FFD2D1 25%, #FFB0AC 50%, #FFA19C 75%, #F2B3FF 100%)',
    supportMessageBackground: '#dde6f6', // Soft tint of Spiris blue-700 (#3c57b9), distinct from coral user bubble

    // Shadows
    shadowCard: 'rgb(0 0 0 / 25%) 0 2px 4px -1px',
    shadowCardSubtle: 'rgb(0 0 0 / 10%) 0 2px 4px -1px',

    // Send Button
    sendButtonActiveIconColor: '#ffffff', // white icon on purple

    // Guidance Card Accordion
    hoverAccordionBackground: '#F2E7FF', // primary-100
};

export const Breakpoints = {
    small: (style: GriffelStyle): Record<string, GriffelStyle> => {
        return { '@media (max-width: 744px)': style };
    },
};

export const ScrollBarStyles: GriffelStyle = {
    overflowY: 'auto',
    '&:hover': {
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: tokens.colorScrollbarOverlay,
            visibility: 'visible',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: tokens.colorNeutralBackground1,
            WebkitBoxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.1)',
            visibility: 'visible',
        },
    },
};

export const SharedStyles: Record<string, GriffelStyle> = {
    scroll: {
        height: '100%',
        ...ScrollBarStyles,
        overscrollBehavior: 'contain',
    },
    overflowEllipsis: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
};

export const useSharedClasses = makeStyles({
    informativeView: {
        display: 'flex',
        flexDirection: 'column',
        padding: '80px',
        alignItems: 'center',
        gap: tokens.spacingVerticalXL,
        marginTop: tokens.spacingVerticalXXXL,
    },
});

export const useDialogClasses = makeStyles({
    surface: {
        paddingRight: tokens.spacingVerticalXS,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
    },
    paragraphs: {
        marginTop: tokens.spacingHorizontalS,
    },
    innerContent: {
        height: '100%',
        ...SharedStyles.scroll,
        paddingRight: tokens.spacingVerticalL,
    },
    text: {
        whiteSpace: 'pre-wrap',
        textOverflow: 'wrap',
    },
    footer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        minWidth: '175px',
    },
});
